import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Sparkles, AlertTriangle, Wrench, Brain, Target, Factory, Pill, Car, ShoppingCart, Droplet, Building, Fuel, Bath, PaintBucket } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

// Logo
const LOGO_URL = "/logo.svg";

// Industry Images — brand professional
const IMAGES = {
  paints: "/images/brand_paint_industry.png",
  pharma: "/images/industry_pharma.png",
  auto: "/images/industry_auto.png",
  fmcg: "/images/industry_fmcg.png",
  petroleum: "/images/industry_steel.png",
  lubricants: "/images/industry_steel.png",
  cement: "/images/brand_cement_industry.png",
  agro: "/images/industry_fmcg.png",
};




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// List component to avoid babel issues
const BulletList = ({ items, color }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
        <span className={`mt-1 ${color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-500' : 'text-[#2C39B8]'}`}>
          {color === 'red' ? '•' : color === 'green' ? '✓' : '⚡'}
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

// Industry Card Component
const IndustrySection = ({ id, icon: Icon, color, title, image, problem, solution, ai, outcomes, isAlt, solutionsFilter }) => (
  <section className={`py-16 ${isAlt ? 'bg-gray-50' : 'bg-white'}`} id={id}>
    <div className="container">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <img src="/logo-icon.png" alt="ChannelLoyalty" className="w-10 h-10 object-contain" />
        <div>
          <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">{title}</h2>
          {solutionsFilter && (
            <Link to={"/solutions?industry=" + encodeURIComponent(solutionsFilter)} className="text-sm text-[#2C39B8] hover:text-[#2C39B8] font-medium flex items-center gap-1 mt-1">
              View all {solutionsFilter} loyalty solutions <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>

      {/* Image if available */}
      {image && (
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
          <img src={image} alt={title} className="w-full h-auto" />
        </div>
      )}

      {/* Content Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Problem */}
        <div className="bg-white rounded-xl p-5 border border-red-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-red-500" />
            <h4 className="font-bold text-gray-900 text-sm">Industry Reality</h4>
          </div>
          <BulletList items={problem} color="red" />
        </div>

        {/* Solution */}
        <div className="bg-white rounded-xl p-5 border border-green-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={18} className="text-green-500" />
            <h4 className="font-bold text-gray-900 text-sm">ChannelLoyalty Solution</h4>
          </div>
          <BulletList items={solution} color="green" />
        </div>

        {/* AI */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-xl p-5 border border-blue-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={18} className="text-[#2C39B8]" />
            <h4 className="font-bold text-gray-900 text-sm">AI Intelligence</h4>
          </div>
          <BulletList items={ai} color="#2C39B8" />
        </div>

        {/* Outcomes */}
        <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Target size={18} className="text-white" />
            <h4 className="font-bold text-white text-sm">Strategic Outcomes</h4>
          </div>
          <ul className="space-y-2">
            {outcomes.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                <span className="text-green-400 mt-1">→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Industry Navigation Pills
const IndustryNav = () => (
  <div className="flex flex-wrap justify-center gap-2 mt-8">
    <a href="#paints" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Paints</a>
    <a href="#pharma" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Pharma</a>
    <a href="#auto" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Auto</a>
    <a href="#fmcg" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">FMCG</a>
    <a href="#plumbing" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Plumbing</a>
    <a href="#cement" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Cement</a>
    <a href="#lubricants" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Lubricants</a>
    <a href="#petroleum" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Petroleum</a>
    <a href="#tiles" className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-all">Tiles</a>
  </div>
);

export default function IndustriesPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <div data-testid="industries-page">
      <Navbar onDemoClick={() => setDemoModalOpen(true)} />
      <SEOHead title="Industry Solutions - Cement, Paints, FMCG, Pharma, Auto Parts & More" description="Industry-specific B2B channel loyalty programs: Cement dealer rewards, paint contractor loyalty, FMCG retailer incentives, pharma distributor programs, auto parts mechanic engagement." keywords="cement dealer loyalty, paint contractor rewards, FMCG retailer loyalty, pharma distributor incentives, auto parts loyalty India" canonical="https://channelloyalty.ai/industries" />
      
      <main>
        {/* Banner Hero */}
        <section className="hero-banner-full">
          <div className="hero-banner-carousel">
            <div className="hero-banner-track hero-banner-track--crop">
              <div className="hero-banner-slide active">
                <img src="/images/banner_industries.jpg" alt="ChannelLoyalty Industries - Purpose-Built for Your Industry" />
              </div>
            </div>
          </div>
        </section>

        {/* PAINTS & COATINGS */}
        <IndustrySection
          id="paints"
          icon={PaintBucket}
          color="#2C39B8"
          title="Paints & Coatings"
          image={IMAGES.paints}
          solutionsFilter="Paints & Coatings"
          problem={[
            "Painter/contractor influence drives brand selection; billing happens elsewhere",
            "Dealer push is scheme-driven → frequent switching",
            "Project-led demand is long-cycle; attribution is weak",
            "Training exists, but ROI is unknown",
            "Leakage and fake claims are common"
          ]}
          solution={[
            "Painter/Contractor Loyalty with skill tiers & verified training",
            "Project Registration with multi-stakeholder mapping",
            "Dealer/Retailer Alignment with progressive slabs",
            "New Product Push Engine (region-specific)",
            "Referral-based rewards system"
          ]}
          ai={[
            "Influence-to-sale attribution (Painter → Project → Dealer)",
            "Training impact analytics (uplift vs spend)",
            "Substitution risk detection",
            "Leakage/fraud anomaly detection",
            "Project pipeline forecasting"
          ]}
          outcomes={[
            "Higher contractor stickiness",
            "Faster NPI adoption",
            "Reduced leakage, improved ROI",
            "Better regional penetration"
          ]}
          isAlt={false}
        />

        {/* PHARMACEUTICALS */}
        <IndustrySection
          id="pharma"
          icon={Pill}
          color="cyan"
          title="Pharmaceuticals (Generics / Branded / OTC)"
          image={IMAGES.pharma}
          solutionsFilter="Pharmaceuticals"
          problem={[
            "Coverage and availability matter more than invoices",
            "Chemist substitution drives outcomes",
            "Dormant outlets kill growth quietly",
            "Compliance constraints demand auditable programs",
            "Expiry/slow-moving inventory creates losses"
          ]}
          solution={[
            "Distributor Velocity + Discipline incentives",
            "Chemist/Retailer Availability Programs",
            "Territory Coverage Engine (beat-level activation)",
            "Education & Compliance Rewards",
            "Dormant outlet reactivation programs"
          ]}
          ai={[
            "Dormancy prediction and churn risk",
            "Territory heatmaps (coverage gaps)",
            "Secondary vs tertiary divergence detection",
            "Expiry-risk forecasting",
            "Scheme effectiveness modeling"
          ]}
          outcomes={[
            "Stronger coverage discipline",
            "Fewer dormant outlets",
            "Reduced expiries",
            "Compliance-safe operations"
          ]}
          isAlt={true}
        />

        {/* AUTO AFTERMARKET */}
        <IndustrySection
          id="auto"
          icon={Car}
          color="pink"
          title="Auto Aftermarket (Parts / Tyres / Batteries)"
          image={IMAGES.auto}
          solutionsFilter="Auto Parts & Lubricants"
          problem={[
            "Mechanics/garages drive recommendations; influence invisible",
            "High brand switching; promotions rarely create loyalty",
            "SKU mix and upsell is inconsistent",
            "Training exists but lacks measurement",
            "Garage churn is silent"
          ]}
          solution={[
            "Mechanic/Garage Loyalty with verified referrals",
            "Retailer/Distributor Alignment with SKU mix incentives",
            "Vehicle-Segment Programs (2W/4W/CV mapping)",
            "Campaign Engine for seasonal peaks",
            "Training certification rewards"
          ]}
          ai={[
            "Mechanic influence-to-sale attribution",
            "Garage churn risk prediction",
            "SKU mix optimization recommendations",
            "Training → upsell correlation analysis",
            "Territory trend detection"
          ]}
          outcomes={[
            "Higher mechanic stickiness",
            "Premium upsell growth",
            "Reduced competitor substitution",
            "Predictable territory growth"
          ]}
          isAlt={false}
        />

        {/* FMCG */}
        <IndustrySection
          id="fmcg"
          icon={ShoppingCart}
          color="orange"
          title="FMCG (Distribution + Retail Execution)"
          image={IMAGES.agro}
          solutionsFilter="FMCG"
          problem={[
            "Growth driven by velocity + shelf execution, not sell-in",
            "Distributor dependency and scheme fatigue common",
            "SKU mix collapses to fast movers",
            "Visibility compliance hard to measure",
            "Trade spend ROI unclear"
          ]}
          solution={[
            "Distributor Velocity Programs (secondary sales, beat penetration)",
            "Retailer Execution Programs (visibility, planogram)",
            "SKU Mix Engine (strategic SKU push)",
            "Seasonal/Festival Campaigns",
            "Range expansion incentives"
          ]}
          ai={[
            "Velocity analytics and drop-off detection",
            "SKU mix deviation detection",
            "Visibility compliance → uplift correlation",
            "Distributor dependency scoring",
            "Scheme fatigue monitoring"
          ]}
          outcomes={[
            "Faster throughput",
            "Stronger shelf dominance",
            "Better range performance",
            "Real-time trade intelligence"
          ]}
          isAlt={true}
        />

        {/* PLUMBING */}
        <IndustrySection
          id="plumbing"
          icon={Droplet}
          color="blue"
          title="Water Tanks / Pipes / Plumbing"
          image={null}
          solutionsFilter="Pipes & Sanitaryware"
          problem={[
            "Plumbers/contractors influence choice",
            "Purchases happen via dealers/retailers",
            "Project-wise attribution is missing",
            "Training and installer loyalty unmanaged",
            "Brand substitution at retailer level frequent"
          ]}
          solution={[
            "Plumber/Contractor Loyalty with certification",
            "Project Tagging (site, society, builder)",
            "Retailer/Dealer Alignment with mix incentives",
            "Warranty/Installation Proof capture",
            "Referral tracking system"
          ]}
          ai={[
            "Project-level attribution",
            "Contractor advocacy effectiveness scoring",
            "Training impact analytics",
            "Substitution risk alerts by territory",
            "Installation quality correlation"
          ]}
          outcomes={[
            "Higher influencer stickiness",
            "Measurable project wins",
            "Reduced substitution",
            "Better penetration and repeat"
          ]}
          isAlt={false}
        />

        {/* CEMENT */}
        <IndustrySection
          id="cement"
          icon={Building}
          color="gray"
          title="Cement"
          image={IMAGES.cement}
          solutionsFilter="Cement"
          problem={[
            "Masons/contractors decide; dealer billing isn't whole story",
            "Project-based demand hard to track",
            "Leakage high due to manual claims",
            "Dealer schemes don't build long-term loyalty",
            "Attribution across project lifecycle weak"
          ]}
          solution={[
            "Mason/Contractor Loyalty with project registration",
            "Dealer/Retailer Volume + Mix incentives",
            "Site-Level Programs (project milestones)",
            "Training + Certification rewards",
            "Verified action tracking"
          ]}
          ai={[
            "Project-to-sales attribution",
            "Mason churn prediction",
            "Leakage anomaly detection",
            "ROI by project type/size",
            "Geographic performance mapping"
          ]}
          outcomes={[
            "Higher project capture",
            "Stronger influencer loyalty",
            "Reduced leakage",
            "Better scheme ROI"
          ]}
          isAlt={true}
        />

        {/* LUBRICANTS */}
        <IndustrySection
          id="lubricants"
          icon={Fuel}
          color="green"
          title="Lubricants & Auto Fluids"
          image={IMAGES.lubricants}
          solutionsFilter="Lubricants"
          problem={[
            "Mechanics influence; purchase via retailers",
            "SKU mix and premium upgrades inconsistent",
            "High switching; program fatigue common",
            "Training impact not measured",
            "Recommendation patterns invisible"
          ]}
          solution={[
            "Mechanic/Garage Loyalty (actions, training, referrals)",
            "Retailer/Distributor Programs (mix + rotation)",
            "Segment campaigns (2W/4W/CV)",
            "Premiumization incentives",
            "Verified upsell tracking"
          ]}
          ai={[
            "Influence-to-sale attribution",
            "Premium upsell analytics",
            "Churn prediction",
            "Fraud detection (duplicate claims)",
            "Recommendation pattern analysis"
          ]}
          outcomes={[
            "Premium mix uplift",
            "Mechanic stickiness",
            "Reduced switching",
            "Higher scheme ROI"
          ]}
          isAlt={false}
        />

        {/* PETROLEUM */}
        <IndustrySection
          id="petroleum"
          icon={Fuel}
          color="#2C39B8"
          title="Petroleum (Fuel Retail + B2B Fleets)"
          image={IMAGES.petroleum}
          solutionsFilter="Petroleum"
          problem={[
            "Outlet throughput varies by dealer discipline",
            "Compliance and reporting critical",
            "Cross-sell under-utilized",
            "Programs often reward volume only",
            "Performance benchmarking missing"
          ]}
          solution={[
            "Dealer Performance Programs (throughput + compliance)",
            "B2B Fleet Loyalty (invoice-based + recurring)",
            "Cross-sell campaigns (lubricants, services)",
            "Regional benchmarking & targets",
            "Compliance-linked incentives"
          ]}
          ai={[
            "Outlet benchmarking and outlier detection",
            "Compliance risk prediction",
            "Cross-sell opportunity modeling",
            "Throughput vs incentive ROI",
            "Performance trend analysis"
          ]}
          outcomes={[
            "Higher dealer discipline",
            "Increased throughput",
            "Reduced compliance risk",
            "Better cross-sell margins"
          ]}
          isAlt={true}
        />

        {/* TILES */}
        <IndustrySection
          id="tiles"
          icon={Bath}
          color="cyan"
          title="Tiles / Sanitaryware / Bath Fittings"
          image={null}
          solutionsFilter="Tiles & Sanitaryware"
          problem={[
            "Architects/contractors influence; purchase via dealer",
            "Display quality drives selection",
            "Project cycle is long; attribution weak",
            "Substitution common based on availability",
            "Design preference tracking missing"
          ]}
          solution={[
            "Architect/Contractor Loyalty with project tagging",
            "Retailer/Dealer Display Compliance programs",
            "Range/Mix Incentives for strategic SKUs",
            "Design training & certification rewards",
            "Long-cycle reward structures"
          ]}
          ai={[
            "Project funnel analytics",
            "Display compliance → uplift correlation",
            "Substitution risk & availability alerts",
            "Influence effectiveness by project type",
            "Design trend analysis"
          ]}
          outcomes={[
            "Higher project conversion",
            "Stronger brand preference",
            "Better display discipline",
            "Predictable demand capture"
          ]}
          isAlt={false}
        />

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-white text-3xl md:text-4xl font-bold mb-6">Your Industry, Your Rules</h2>
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                ChannelLoyalty adapts to your channel reality. Let us show you how we solve for your specific industry challenges.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                  Request Industry Demo <ArrowRight size={18} />
                </button>
                <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                  <MessageCircle size={18} /> Talk to Expert
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
}
